import { toDate } from "../../utils/date";

export type ReviewStatus = "completed" | "overdue" | "due_now" | "upcoming" | "future";

export interface ReviewWithStatus {
  id: string;
  day: number;
  due_date: string;
  status: string;
  completed_at?: string;
}

/**
 * Classifies a review based on its due date and completion status
 * This is the single source of truth for review classification
 */
export function classifyReview(review: ReviewWithStatus): ReviewStatus {
  if (review.completed_at) return "completed";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // due_date is a bare calendar date (YYYY-MM-DD). toDate anchors it at local
  // midnight so the comparison against `today` is a same-zone calendar
  // comparison rather than a UTC one.
  const dueDate = toDate(review.due_date);
  dueDate.setHours(0, 0, 0, 0);

  if (dueDate < today) return "overdue";
  if (dueDate.getTime() === today.getTime()) return "due_now";

  const nextWeek = new Date(today);
  nextWeek.setDate(today.getDate() + 7);
  if (dueDate <= nextWeek) return "upcoming";

  return "future"; // more than 7 days away
}

/**
 * Classifies all reviews for a decision and returns categorized reviews
 */
export function classifyDecisionReviews(decision: { 
  scheduled_reviews: ReviewWithStatus[] 
}) {
  const dueNowReviews = decision.scheduled_reviews.filter(r => classifyReview(r) === "due_now");
  const overdueReviews = decision.scheduled_reviews.filter(r => classifyReview(r) === "overdue");
  const upcomingReviews = decision.scheduled_reviews.filter(r => classifyReview(r) === "upcoming");
  const completedReviews = decision.scheduled_reviews.filter(r => classifyReview(r) === "completed");
  const futureReviews = decision.scheduled_reviews.filter(r => classifyReview(r) === "future");

  return {
    dueNowReviews,
    overdueReviews,
    upcomingReviews,
    completedReviews,
    futureReviews,
  };
}

/**
 * Gets the count of reviews by status across all decisions
 */
export function getReviewCounts(decisions: Array<{ scheduled_reviews: ReviewWithStatus[] }>) {
  const classifiedDecisions = decisions.map(classifyDecisionReviews);
  
  return {
    dueNowCount: classifiedDecisions.reduce((sum, d) => sum + d.dueNowReviews.length, 0),
    overdueCount: classifiedDecisions.reduce((sum, d) => sum + d.overdueReviews.length, 0),
    upcomingCount: classifiedDecisions.reduce((sum, d) => sum + d.upcomingReviews.length, 0),
    completedCount: classifiedDecisions.reduce((sum, d) => sum + d.completedReviews.length, 0),
    futureCount: classifiedDecisions.reduce((sum, d) => sum + d.futureReviews.length, 0),
  };
}